import { useState } from 'react';
import type { HealthMetricTrend } from '../../types/health';
import { pickLocalized, useLang } from '../../i18n';

interface MetricLineChartProps {
  series: HealthMetricTrend;
  height?: number;
}

const WIDTH = 640;
const PAD_X = 44;
const PAD_Y = 28;
const CHART_START_TIME = Date.parse('2020-01-01T00:00:00');

export default function MetricLineChart({ series, height = 180 }: MetricLineChartProps) {
  const { lang } = useLang();
  const [activePointKey, setActivePointKey] = useState<string | null>(null);
  const points = [...series.points]
    .filter((point) => Number.isFinite(point.value) && !Number.isNaN(Date.parse(point.date)))
    .sort((a, b) => Date.parse(a.date) - Date.parse(b.date));

  if (points.length === 0) {
    return (
      <div className="flex h-44 items-center justify-center rounded-lg border border-dashed border-neutral-300 bg-neutral-50 text-sm text-coffee/70">
        {pickLocalized(series.label, lang)}
      </div>
    );
  }

  const values = points.map((point) => point.value);
  const parsedRange = parseReferenceRange(series.referenceRange);
  const rangeValues = parsedRange ? [parsedRange.min, parsedRange.max].filter((value) => Number.isFinite(value)) : [];
  const minValue = Math.min(...values, ...rangeValues);
  const maxValue = Math.max(...values, ...rangeValues);
  const yPadding = minValue === maxValue ? Math.max(1, Math.abs(minValue) * 0.1) : (maxValue - minValue) * 0.14;
  const yMin = minValue - yPadding;
  const yMax = maxValue + yPadding;
  const pointTimes = points.map((point) => Date.parse(point.date));
  const firstTime = Math.min(CHART_START_TIME, ...pointTimes);
  const lastTime = Math.max(Date.now(), ...pointTimes);
  const timeSpan = Math.max(1, lastTime - firstTime);
  const chartWidth = WIDTH - PAD_X * 2;
  const chartHeight = height - PAD_Y * 2;
  const yForValue = (value: number) => PAD_Y + (1 - (value - yMin) / Math.max(1, yMax - yMin)) * chartHeight;
  const rangeBand = parsedRange
    ? {
      top: yForValue(parsedRange.max),
      bottom: yForValue(parsedRange.min),
    }
    : null;
  const pointPositions = points.map((point) => {
    const x = points.length === 1
      ? WIDTH / 2
      : PAD_X + ((Date.parse(point.date) - firstTime) / timeSpan) * chartWidth;
    const y = yForValue(point.value);
    return { ...point, x, y };
  });
  const path = pointPositions
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');
  const activePoint = pointPositions.find((point) => pointKey(point) === activePointKey);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height}`}
      role="img"
      aria-label={`${pickLocalized(series.label, lang)} ${series.points.length}`}
      className="h-44 w-full overflow-visible"
    >
      <line x1={PAD_X} y1={PAD_Y} x2={PAD_X} y2={height - PAD_Y} stroke="currentColor" className="text-neutral-200" />
      <line x1={PAD_X} y1={height - PAD_Y} x2={WIDTH - PAD_X} y2={height - PAD_Y} stroke="currentColor" className="text-neutral-200" />
      {rangeBand && (
        <g aria-hidden="true">
          <rect
            x={PAD_X}
            y={rangeBand.top}
            width={chartWidth}
            height={Math.max(1, rangeBand.bottom - rangeBand.top)}
            rx="6"
            className="fill-emerald-100/45"
          />
          <line x1={PAD_X} y1={rangeBand.top} x2={WIDTH - PAD_X} y2={rangeBand.top} stroke="currentColor" strokeDasharray="5 5" className="text-emerald-600/60" />
          <line x1={PAD_X} y1={rangeBand.bottom} x2={WIDTH - PAD_X} y2={rangeBand.bottom} stroke="currentColor" strokeDasharray="5 5" className="text-emerald-600/60" />
          <text x={WIDTH - PAD_X} y={Math.max(12, rangeBand.top - 5)} textAnchor="end" className="fill-emerald-700/70 text-[10px] font-mono">
            range
          </text>
        </g>
      )}
      <text x={PAD_X} y={18} className="fill-coffee/70 text-[11px] font-mono">
        {formatValue(yMax)}
      </text>
      <text x={PAD_X} y={height - 8} className="fill-coffee/70 text-[11px] font-mono">
        {formatValue(yMin)}
      </text>
      <text x={PAD_X} y={height - 1} className="fill-coffee/60 text-[10px] font-mono">
        {formatShortDate(new Date(firstTime).toISOString())}
      </text>
      <text x={WIDTH - PAD_X} y={height - 1} textAnchor="end" className="fill-coffee/60 text-[10px] font-mono">
        {formatShortDate(new Date(lastTime).toISOString())}
      </text>
      <path d={path} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-tan" />
      {pointPositions.map((point) => {
        const key = pointKey(point);
        return (
          <g
            key={key}
            tabIndex={0}
            role="button"
            aria-label={`${formatLongDate(point.date)} ${formatValue(point.value)}${series.unit ? ` ${series.unit}` : ''}`}
            className="cursor-crosshair outline-none"
            onMouseEnter={() => setActivePointKey(key)}
            onMouseLeave={() => setActivePointKey(null)}
            onFocus={() => setActivePointKey(key)}
            onBlur={() => setActivePointKey(null)}
          >
            <circle
              cx={point.x}
              cy={point.y}
              r="14"
              className="fill-transparent"
            />
            <circle
              cx={point.x}
              cy={point.y}
              r={point.approximate ? 5 : 4}
              className={point.status === 'high' || point.status === 'low' || point.status === 'abnormal' ? 'fill-cocoa' : 'fill-tan'}
            />
            {point.approximate && (
              <circle cx={point.x} cy={point.y} r="8" fill="none" stroke="currentColor" strokeDasharray="2 3" className="text-cocoa/50" />
            )}
          </g>
        );
      })}
      {activePoint && <Tooltip point={activePoint} unit={series.unit} height={height} />}
    </svg>
  );
}

interface PositionedPoint {
  date: string;
  value: number;
  x: number;
  y: number;
  approximate?: boolean;
}

function Tooltip({ point, unit, height }: { point: PositionedPoint; unit?: string; height: number }) {
  const tooltipWidth = 156;
  const tooltipHeight = 48;
  const x = Math.min(Math.max(point.x - tooltipWidth / 2, PAD_X), WIDTH - PAD_X - tooltipWidth);
  const y = point.y > PAD_Y + tooltipHeight + 12 ? point.y - tooltipHeight - 12 : point.y + 14;

  return (
    <g pointerEvents="none">
      <line
        x1={point.x}
        y1={PAD_Y}
        x2={point.x}
        y2={height - PAD_Y}
        stroke="currentColor"
        strokeDasharray="3 4"
        className="text-coffee/20"
      />
      <rect
        x={x}
        y={y}
        width={tooltipWidth}
        height={tooltipHeight}
        rx="10"
        className="fill-white stroke-neutral-200 drop-shadow-sm"
      />
      <text x={x + 12} y={y + 19} className="fill-coffee/70 text-[11px] font-mono">
        X {formatLongDate(point.date)}
      </text>
      <text x={x + 12} y={y + 36} className="fill-cocoa text-[12px] font-mono font-semibold">
        Y {formatValue(point.value)}{unit ? ` ${unit}` : ''}{point.approximate ? ' ≈' : ''}
      </text>
    </g>
  );
}

function pointKey(point: { date: string; value: number }): string {
  return `${point.date}:${point.value}`;
}

function parseReferenceRange(value: string | undefined): { min: number; max: number } | null {
  if (!value) return null;

  const normalized = value
    .replace(/[—–~～至]/g, '-')
    .replace(/,/g, '')
    .trim();
  if (/[<>]/.test(normalized)) return null;

  const match = normalized.match(/(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)/);
  if (!match) return null;

  const min = Number(match[1]);
  const max = Number(match[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) return null;
  return { min, max };
}

function formatValue(value: number): string {
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function formatShortDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatLongDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
